<template>
  <div>
    <v-dialog :modelValue="dialog" @click:outside="$emit('close')" @keydown.esc="$emit('close')" max-width="1400px" transition="dialog-bottom-transition">
      <v-card>
        <v-form @submit.prevent>
          <v-card-title>
            <span class="headline">Simular / Aplicar políticas de reposición</span>
          </v-card-title>

          <v-card-text>
            <div class="px-2 py-1 mb-3 bg-subtitle-section">
              <p class="text-subtitle-1 mb-0"><b>Ejecutar políticas</b></p>
            </div>
            <p class="text-body-2 mb-1">Calcula el mínimo y máximo de las reglas según la demanda real y asigna ubicaciones de picking. La simulación no guarda nada.</p>
            <p class="text-caption text-medium-emphasis mb-3">Las políticas activas también se aplican automáticamente una vez por mes. Las reglas manuales no se modifican.</p>

            <v-row align="center">
              <v-col cols="12" md="4">
                <v-combobox
                  v-model="almacen"
                  :items="almacenes"
                  item-title="name"
                  item-value="id"
                  label="Almacén"
                  density="compact"
                  clearable
                  hide-details
                  placeholder="Todos los almacenes"
                  persistent-placeholder
                  :disabled="running || loading_start"
                ></v-combobox>
              </v-col>
              <v-col cols="12" md="8" class="d-flex align-center flex-wrap ga-3">
                <v-btn
                  v-if="can_simulate"
                  flat
                  class="btn-second"
                  prepend-icon="mdi-test-tube"
                  :loading="loading_start && dry_run_solicitado"
                  :disabled="running || loading_start"
                  @click="iniciar(true)"
                >
                  Simular
                </v-btn>
              </v-col>
            </v-row>

            <!-- Resultado de la ultima corrida (simulacion o aplicacion; la mensual tambien) -->
            <div class="px-2 py-1 mb-3 mt-6 bg-subtitle-section">
              <p class="text-subtitle-1 mb-0"><b>Resultado de la última corrida</b></p>
            </div>

            <div v-if="running" class="mb-3">
              <v-progress-linear :indeterminate="porcentaje === null" :model-value="porcentaje || 0"></v-progress-linear>
              <p class="text-body-2 text-medium-emphasis mt-1 mb-0">{{ last_result_en_curso_texto }}</p>
            </div>

            <div v-if="last_result" :style="{ opacity: running ? 0.5 : 1 }">
              <div class="d-flex align-center flex-wrap ga-3 mb-1">
                <p class="text-body-2 mb-0">
                  <b>{{ last_result.dry_run ? "Simulación" : "Aplicación" }}</b>
                  · {{ last_result.almacen_id ? getAlmacenName(last_result.almacen_id) : "Todos los almacenes" }}
                  <template v-if="last_result.finished_at || last_result.started_at"> · {{ last_result.finished_at || last_result.started_at }}</template>
                  <template v-if="last_result.contadores">
                    — {{ contador("evaluados") }} evaluados: <b>{{ contador("creadas") }} {{ label_creadas }}, {{ contador("actualizadas") }} {{ label_actualizadas }}</b>,
                    {{ contador("sin_cambios") }} sin cambios, {{ contador("salteadas") }} salteados,
                    <span :class="{ 'text-error font-weight-bold': contador('errores') > 0 }">{{ contador("errores") }} con error</span>
                  </template>
                </p>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="can_apply && puede_aplicar"
                  color="success"
                  prepend-icon="mdi-check-all"
                  :loading="loading_start && !dry_run_solicitado"
                  :disabled="running || loading_start"
                  @click="dialog_confirm_aplicar = true"
                >
                  Aplicar
                </v-btn>
              </div>
              <p v-if="!last_result.ok && last_result.msg" class="text-body-2 text-error mb-1">{{ last_result.msg }}</p>
              <!-- El resultado queda guardado tal como termino la corrida: no se entera si despues se
                   editan o borran reglas, asi que se aclara para que no se lea como el estado actual -->
              <p class="text-caption text-medium-emphasis mb-1">No refleja ediciones posteriores a la corrida; simulá de nuevo para actualizarlo.</p>
              <p v-if="last_result.dry_run && last_result.ok" class="text-caption text-medium-emphasis mb-1">Los cambios de la simulación no se guardaron.</p>

              <v-row align="center" dense class="mb-1 mt-2" v-if="detalle_total > 0">
                <v-col cols="12" md="9" class="d-flex align-center flex-wrap ga-2">
                  <!-- Los grupos se combinan (accion Y demanda Y observaciones); las cantidades son las del resultado completo -->
                  <span class="text-caption text-medium-emphasis">Acción</span>
                  <v-chip-group :model-value="filtro_accion" mandatory selected-class="text-primary" class="py-0" @update:modelValue="setFiltro('filtro_accion', $event)">
                    <template v-for="a in acciones_filtro" :key="a.id">
                      <v-chip v-if="a.id == FILTRO_TODAS || cantidad(a.cantidad) > 0" :value="a.id" variant="outlined" size="small">
                        {{ a.name }} ({{ cantidad(a.cantidad) }})
                      </v-chip>
                    </template>
                  </v-chip-group>
                  <span class="text-caption text-medium-emphasis ms-2">Demanda</span>
                  <v-chip-group :model-value="filtro_demanda" mandatory selected-class="text-primary" class="py-0" @update:modelValue="setFiltro('filtro_demanda', $event)">
                    <template v-for="d in demandas_filtro" :key="d.id">
                      <v-chip v-if="d.id == FILTRO_TODAS || cantidad(d.cantidad) > 0" :value="d.id" variant="outlined" size="small">
                        {{ d.name }} ({{ cantidad(d.cantidad) }})
                      </v-chip>
                    </template>
                  </v-chip-group>
                  <v-chip
                    v-if="cantidad('con_observaciones') > 0"
                    variant="outlined"
                    size="small"
                    :class="{ 'text-primary': filtro_observaciones }"
                    @click="setFiltro('filtro_observaciones', !filtro_observaciones)"
                  >
                    Con observaciones ({{ cantidad("con_observaciones") }})
                  </v-chip>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="search"
                    append-inner-icon="mdi-magnify"
                    single-line
                    hide-details
                    label="Buscar"
                    density="compact"
                    @keydown.enter.prevent="recargarDesdeInicio"
                    @click:append-inner="recargarDesdeInicio"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Paginacion y orden en el server: la tabla solo muestra la pagina que llega, sin reordenarla -->
              <v-data-table-server
                v-if="detalle_total > 0"
                :headers="headers"
                :items="detalle"
                :items-length="total"
                :items-per-page="items_per_page"
                :page="current_page"
                :sort-by="sort_by"
                :loading="loading_detalle"
                density="compact"
                hide-default-footer
                loading-text="Cargando..."
                @update:sortBy="onSort"
              >
                <template v-slot:no-data>
                  <span class="text-medium-emphasis">Sin filas para el filtro aplicado.</span>
                </template>
                <template v-slot:item="{ item }">
                  <tr>
                    <td>
                      <v-btn icon variant="text" size="small" @click="toggleExpand(getKey(item))">
                        <v-icon>{{ expandedRows.includes(getKey(item)) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                      </v-btn>
                    </td>
                    <td style="min-width: 200px">
                      {{ item.product ? item.product.name : "-" }}
                      <span class="text-caption text-grey-600 d-block">SKU: {{ item.product ? item.product.sku : "-" }}</span>
                    </td>
                    <td v-if="!last_result.almacen_id">{{ getAlmacenName(getItemAlmacenId(item)) }}</td>
                    <td :class="item.accion == 'error' ? 'text-error' : 'text-medium-emphasis'">{{ getAccionName(item.accion) }}</td>
                    <td class="text-center" style="white-space: nowrap">
                      <template v-if="item.stock_min_antes != null || item.stock_max_antes != null">
                        <span class="text-medium-emphasis">{{ valor(item.stock_min_antes) }} / {{ valor(item.stock_max_antes) }}</span>
                        <v-icon size="x-small" class="mx-1">mdi-arrow-right</v-icon>
                      </template>
                      <b>{{ valor(item.stock_min_despues) }} / {{ valor(item.stock_max_despues) }}</b>
                    </td>
                    <td>
                      <div v-if="tieneUbicaciones(item)">
                        <span v-if="item.ubicaciones_agregadas && item.ubicaciones_agregadas.length > 0" class="d-block">
                          <span v-for="(code, i) in item.ubicaciones_agregadas" :key="code" class="text-no-wrap">{{ i == 0 ? "+ " : "" }}{{ code }}{{ i < item.ubicaciones_agregadas.length - 1 ? ", " : "" }}</span>
                        </span>
                        <span v-if="item.ubicaciones_quitadas && item.ubicaciones_quitadas.length > 0" class="d-block text-medium-emphasis">
                          <span v-for="(code, i) in item.ubicaciones_quitadas" :key="code" class="text-no-wrap">{{ i == 0 ? "− " : "" }}{{ code }}{{ i < item.ubicaciones_quitadas.length - 1 ? ", " : "" }}</span>
                        </span>
                      </div>
                      <span v-else>-</span>
                      <div v-if="item.accion == 'error' && item.mensaje" class="text-caption text-error">{{ item.mensaje }}</div>
                      <div v-if="item.observacion" class="text-caption">
                        <v-icon size="x-small" color="warning">mdi-alert-outline</v-icon>
                        {{ item.observacion }}
                      </div>
                    </td>
                    <td class="text-center text-medium-emphasis">
                      {{ formatDecimal(item.demanda_diaria) }}
                      <span v-if="item.periodo_dias" class="text-caption d-block">en {{ item.periodo_dias }} días</span>
                    </td>
                  </tr>
                  <tr v-if="expandedRows.includes(getKey(item))">
                    <td :colspan="headers.length" class="bg-grey-lighten-4 px-6 py-3">
                      <p v-for="(linea, i) in lineasCalculo(item)" :key="i" class="text-body-2 mb-1">{{ linea }}</p>
                    </td>
                  </tr>
                </template>
              </v-data-table-server>

              <template v-if="detalle_total > 0">
                <div class="text-right mt-2">
                  <small class="text-grey">{{ paginationText }}</small>
                </div>
                <v-pagination v-if="cant_pages > 1" v-model="current_page" :length="cant_pages" total-visible="10" @update:modelValue="loadDetalle"></v-pagination>
              </template>

              <p v-else-if="!running && last_result.ok" class="text-body-2 text-medium-emphasis text-center my-4">No hubo cambios: las reglas ya están al día.</p>
            </div>

            <p v-else-if="!running" class="text-body-2 text-medium-emphasis text-center my-4">Todavía no hay simulaciones. Elegí un almacén y presioná Simular.</p>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('close')">Cerrar</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <DialogYesNo
      :open="dialog_confirm_aplicar"
      title="Aplicar políticas"
      :message="mensaje_confirmar_aplicar"
      :loading="loading_start"
      @cancel="dialog_confirm_aplicar = false"
      @accept="iniciar(false)"
    />
  </div>
</template>

<script>
import { reposicionRuleService } from "@/libs/ws/reposicionRuleService";

const ACCIONES = [
  { id: "creada", name: "Nueva" },
  { id: "actualizada", name: "Actualiza" },
  { id: "error", name: "Error" },
];
const FILTRO_TODAS = "todas";
//cantidad = clave de last_result.cantidades (el detalle no viaja con el estado, solo el resumen)
const FILTROS_ACCION = [{ id: FILTRO_TODAS, name: "Todas", cantidad: "total" }, ...ACCIONES.map((a) => ({ ...a, cantidad: a.id }))];
const FILTROS_DEMANDA = [
  { id: FILTRO_TODAS, name: "Todas", cantidad: "total" },
  { id: "con_demanda", name: "Con demanda", cantidad: "con_demanda" },
  { id: "sin_historia", name: "Sin historia", cantidad: "sin_historia" },
];

//Se avisa "historia corta" cuando los dias con despacho son menos de este porcentaje de la ventana de la regla
const UMBRAL_HISTORIA_CORTA = 0.25;

export default {
  props: {
    dialog: Boolean,
    almacenes: { type: Array, default: () => [] },
    almacen_default: { type: Object, default: null }, //almacen elegido en el listado de politicas
    can_simulate: Boolean, //simular solo lee: alcanza con permiso de listado
    can_apply: Boolean, //aplicar crea y edita reglas: requiere alta + modificacion
  },
  emits: ["close", "applied", "running"],
  data() {
    return {
      almacen: null,
      loading_start: false, //true mientras se dispara el proceso (POST inicial)
      dry_run_solicitado: true,
      iniciada_aca: false, //la corrida en curso la disparo este dialogo
      running: false, //true mientras el proceso sigue corriendo del lado del server
      last_result: null,
      checking_estado: false, //evita solapar polls si uno anterior no respondio
      poll_interval: null,
      dialog_confirm_aplicar: false,
      progreso: null, //{procesados, total, dry_run, ...} de la corrida en curso; null hasta el primer latido
      search: "",
      FILTRO_TODAS,
      filtro_accion: FILTRO_TODAS,
      filtro_demanda: FILTRO_TODAS,
      filtro_observaciones: false,
      acciones_filtro: FILTROS_ACCION,
      demandas_filtro: FILTROS_DEMANDA,
      sort_by: [], //[{key, order}], se ordena en el server
      expandedRows: [],
      detalle: [], //solo la pagina actual
      total: 0, //filas del detalle con los filtros aplicados
      cant_pages: 0,
      current_page: 1,
      loading_detalle: false,
      detalle_solicitud: 0, //descarta respuestas de pedidos viejos que llegan tarde
    };
  },
  computed: {
    //Identifica la corrida del resultado: al cambiar hay que volver a pedir el detalle (resultados viejos no traen run_id)
    resultado_key() {
      return this.last_result ? this.last_result.run_id || this.last_result.finished_at : null;
    },
    detalle_total() {
      return this.last_result && this.last_result.detalle_total ? this.last_result.detalle_total : 0;
    },
    items_per_page() {
      return this.$store.state.config.items_per_page;
    },
    paginationText() {
      if (this.total === 0) return "No hay resultados";
      const items_per_page = this.items_per_page;
      const desde = (this.current_page - 1) * items_per_page + 1;
      const hasta = Math.min(this.current_page * items_per_page, this.total);
      return `Mostrando ${desde} a ${hasta} de ${this.total} resultados`;
    },
    //null = sin progreso conocido (barra indeterminada)
    porcentaje() {
      if (!this.progreso || !this.progreso.total) return null;
      return Math.min(100, Math.floor((this.progreso.procesados * 100) / this.progreso.total));
    },
    headers() {
      const headers = [
        { title: "", key: "expand", sortable: false, width: "48px" },
        { title: "Producto", key: "product.name", sortable: true, align: "start" },
      ];
      //Con un almacen puntual la columna es redundante: se decide por el alcance de la corrida
      if (!this.last_result || !this.last_result.almacen_id) headers.push({ title: "Almacén", key: "almacen_id", sortable: false, align: "start" });
      headers.push(
        { title: "Acción", key: "accion", sortable: true, align: "start" },
        { title: "Mín / Máx", key: "stock_max_despues", sortable: false, align: "center" },
        { title: "Ubicaciones", key: "ubicaciones", sortable: false, align: "start", minWidth: "160px" },
        { title: "Demanda diaria", key: "demanda_diaria", sortable: true, align: "center" }
      );
      return headers;
    },
    //En simulacion no se guardo nada: los contadores son "a crear / a actualizar"
    label_creadas() {
      return this.last_result && this.last_result.dry_run ? "a crear" : "creadas";
    },
    label_actualizadas() {
      return this.last_result && this.last_result.dry_run ? "a actualizar" : "actualizadas";
    },
    //Solo se ofrece aplicar despues de una simulacion terminada: aplica sobre el mismo alcance
    puede_aplicar() {
      return this.last_result != null && this.last_result.dry_run && this.last_result.ok && this.last_result.finished_at != null && !this.running;
    },
    mensaje_confirmar_aplicar() {
      const alcance = this.last_result && this.last_result.almacen_id ? "del almacén " + this.getAlmacenName(this.last_result.almacen_id) : "de todos los almacenes";
      return (
        "¿Confirma aplicar las políticas de reposición " +
        alcance +
        "? Se crearán y modificarán las reglas de reposición gestionadas por políticas. El proceso corre en segundo plano y puede tardar varios minutos."
      );
    },
    last_result_en_curso_texto() {
      //Si hay progreso, la corrida dice si es simulacion (aunque la haya disparado otro usuario o sea la mensual);
      //antes del primer latido solo se sabe si la disparo este dialogo
      const dry_run = this.progreso ? this.progreso.dry_run : this.iniciada_aca ? this.dry_run_solicitado : null;
      let texto = dry_run === null ? "Procesando políticas..." : dry_run ? "Simulando políticas..." : "Aplicando políticas...";
      if (this.porcentaje !== null) texto += " " + this.progreso.procesados + " de " + this.progreso.total + " productos (" + this.porcentaje + "%)";
      return texto;
    },
  },
  watch: {
    dialog() {
      //Al abrir se refresca el estado: puede haber una corrida disparada por otro usuario o la mensual
      if (!this.dialog) return;
      this.almacen = this.almacen_default;
      this.checkEstadoInicial();
    },
    //Solo al cambiar de corrida: el polling reemplaza last_result cada 5s con el mismo contenido
    resultado_key() {
      this.expandedRows = [];
      this.filtro_accion = FILTRO_TODAS;
      this.filtro_demanda = FILTRO_TODAS;
      this.filtro_observaciones = false;
      this.search = "";
      this.sort_by = [];
      this.detalle = [];
      this.loadDetalle(1);
    },
    running(value) {
      this.$emit("running", value);
    },
  },
  mounted() {
    this.checkEstadoInicial();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    getAlmacenName(almacen_id) {
      const a = this.almacenes.find((x) => x.id == almacen_id);
      return a ? a.name : "#" + almacen_id;
    },
    getAccionName(accion) {
      const a = ACCIONES.find((x) => x.id == accion);
      return a ? a.name : accion;
    },
    //Los resultados guardados antes de que el backend informara almacen_id traian el objeto almacen
    getItemAlmacenId(item) {
      return item.almacen_id || (item.almacen ? item.almacen.id : null);
    },
    cantidad(key) {
      const c = this.last_result && this.last_result.cantidades ? this.last_result.cantidades : {};
      return c[key] != null ? c[key] : 0;
    },
    getKey(item) {
      return (item.product ? item.product.id : "") + "-" + (this.getItemAlmacenId(item) || "");
    },
    setFiltro(filtro, value) {
      this[filtro] = value;
      this.loadDetalle(1);
    },
    onSort(sort_by) {
      this.sort_by = sort_by;
      this.loadDetalle(1);
    },
    recargarDesdeInicio() {
      this.loadDetalle(1);
    },
    //Pide al server la pagina actual del detalle con los filtros, la busqueda y el orden elegidos
    loadDetalle(page = this.current_page) {
      if (!this.resultado_key || this.detalle_total == 0) {
        //Descarta las respuestas en vuelo de pedidos anteriores
        this.detalle_solicitud++;
        this.loading_detalle = false;
        this.detalle = [];
        this.total = 0;
        this.cant_pages = 0;
        return;
      }

      this.current_page = page;
      const items_per_page = this.items_per_page;
      const solicitud = ++this.detalle_solicitud;
      this.loading_detalle = true;

      var param = { from: page, limit: items_per_page };
      if (this.filtro_accion != FILTRO_TODAS) param.accion = this.filtro_accion;
      if (this.filtro_demanda != FILTRO_TODAS) param.demanda = this.filtro_demanda;
      if (this.filtro_observaciones) param.con_observaciones = true;
      if (this.search) param.search = this.search;
      if (this.sort_by.length > 0) {
        param.sort_by = this.sort_by[0].key;
        param.sort_desc = this.sort_by[0].order == "desc";
      }

      reposicionRuleService
        .politicas_detalle(param)
        .then((response) => {
          if (solicitud != this.detalle_solicitud) return;
          if (!response.status) return this.toast.error(response.msg);

          //Resumen y detalle de corridas distintas (termino otra mientras tanto): se vuelve a pedir el estado,
          //que cambia resultado_key y recarga el detalle
          if (this.last_result && response.result.run_id && this.last_result.run_id && response.result.run_id != this.last_result.run_id) {
            this.checkEstado();
            return;
          }

          this.detalle = response.result.list;
          this.total = response.result.total;
          this.cant_pages = Math.ceil(response.result.total / items_per_page);
        })
        .finally(() => {
          if (solicitud == this.detalle_solicitud) this.loading_detalle = false;
        });
    },
    toggleExpand(key) {
      const index = this.expandedRows.indexOf(key);
      if (index > -1) this.expandedRows.splice(index, 1);
      else this.expandedRows.push(key);
    },
    tieneUbicaciones(item) {
      return (item.ubicaciones_agregadas && item.ubicaciones_agregadas.length > 0) || (item.ubicaciones_quitadas && item.ubicaciones_quitadas.length > 0);
    },
    //Tolera resultados guardados antes de que el backend informara periodo_dias
    lineasCalculo(item) {
      const lineas = [];
      if (item.sin_historia) lineas.push("Sin despachos en el período de la regla: se usaron los valores por defecto.");
      else if (item.total_despachado != null && item.dias_operacion != null) {
        const ventana = item.periodo_dias ? "en los últimos " + item.periodo_dias + " días" : "en el período";
        lineas.push(
          "Despachado: " + item.total_despachado + " u. " + ventana + ". El almacén despachó en " + item.dias_operacion + (item.periodo_dias ? " de esos días" : " días") + " → " + this.formatDecimal(item.demanda_diaria) + " u./día."
        );
        if (item.periodo_dias && item.dias_operacion > 0 && item.dias_operacion < item.periodo_dias * UMBRAL_HISTORIA_CORTA)
          lineas.push("Historia corta: pocos días con despacho respecto del período de la regla; la demanda puede no ser representativa.");
      }
      if (item.capacidad != null) lineas.push("Capacidad de las ubicaciones: " + item.capacidad + " u.");
      if (item.mensaje) lineas.push(item.mensaje);
      return lineas;
    },
    contador(key) {
      const c = this.last_result && this.last_result.contadores ? this.last_result.contadores : {};
      return c[key] != null ? c[key] : 0;
    },
    valor(value) {
      return value === null || value === undefined ? "-" : value;
    },
    formatDecimal(value) {
      if (value === null || value === undefined || value === "") return "-";
      return parseFloat(value).toFixed(2);
    },
    iniciar(dry_run) {
      if (this.loading_start || this.running) return; //guard de reentrada
      this.loading_start = true;
      this.dry_run_solicitado = dry_run;

      var param = { dry_run: dry_run };
      if (dry_run) {
        if (this.almacen && this.almacen.id) param.almacen = { id: this.almacen.id };
      } else if (this.last_result && this.last_result.almacen_id) {
        //Se aplica sobre el mismo alcance que se simulo
        param.almacen = { id: this.last_result.almacen_id };
      }

      reposicionRuleService
        .aplicar_politicas(param)
        .then((response) => {
          if (response.status) {
            this.toast.success(response.msg);
            this.iniciada_aca = true;
            this.progreso = null;
            this.running = true;
            this.startPolling();
          } else {
            this.toast.error(response.msg);
          }
        })
        .catch((e) => {
          this.toast.error(e);
        })
        .finally(() => {
          this.loading_start = false;
          this.dialog_confirm_aplicar = false;
        });
    },
    checkEstadoInicial() {
      reposicionRuleService.politicas_estado().then((response) => {
        if (!response.status) return;

        this.last_result = response.result.last_result;
        this.progreso = response.result.progreso;

        if (response.result.running && !this.running) {
          this.iniciada_aca = false;
          this.running = true;
          this.startPolling();
        }
      });
    },
    startPolling() {
      this.stopPolling(); //por si quedo uno viejo corriendo
      this.poll_interval = setInterval(() => {
        this.checkEstado();
      }, 5000);
    },
    stopPolling() {
      if (this.poll_interval) {
        clearInterval(this.poll_interval);
        this.poll_interval = null;
      }
    },
    checkEstado() {
      if (this.checking_estado) return; //no solapar si la respuesta anterior no llego
      this.checking_estado = true;

      reposicionRuleService
        .politicas_estado()
        .then((response) => {
          if (!response.status) return;

          var termino = this.running && !response.result.running;
          this.running = response.result.running;
          this.last_result = response.result.last_result;
          this.progreso = response.result.progreso;

          if (!this.running) {
            this.stopPolling();
            this.iniciada_aca = false;
            if (termino) this.showResult(response.result.last_result);
          }
        })
        .finally(() => {
          this.checking_estado = false;
        });
    },
    showResult(last_result) {
      if (!last_result) {
        this.toast.error("El proceso de políticas terminó sin reportar un resultado. Revise el log del servidor.");
        return;
      }

      const tipo = last_result.dry_run ? "Simulación" : "Aplicación de políticas";
      if (last_result.ok) this.toast.success(tipo + " finalizada. " + (last_result.msg || ""));
      else this.toast.error(tipo + " finalizada con errores. " + (last_result.msg || ""));

      //Una aplicacion real cambia las reglas: el listado tiene que refrescarse
      if (!last_result.dry_run) this.$emit("applied");
    },
  },
};
</script>
